import { useState, type FormEvent } from 'react'
import { KeyRound, ShieldAlert } from 'lucide-react'
import { changePasswordSchema } from '@najah/core'
import { Alert, Button, Card, CardBody, Input, LogoMark, useToast } from '@najah/ui'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'

/**
 * شاشة إجبارية عند أول دخول بكلمة مرور مؤقّتة.
 * الخادم يرفض كل شيء آخر حتى تتغيّر، فلا فائدة من عرض بقيّة التطبيق.
 */
export function ChangePasswordScreen() {
  const { logout, refreshProfile } = useAuth()
  const toast = useToast()

  const [currentPassword, setCurrent] = useState('')
  const [newPassword, setNew] = useState('')
  const [confirmPassword, setConfirm] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)

    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword, confirmPassword })
    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) next[String(issue.path[0] ?? '_')] ??= issue.message
      setErrors(next)
      return
    }

    setErrors({})
    setBusy(true)
    try {
      await api('/auth/change-password', { method: 'POST', body: parsed.data })
      toast.success('تغيّرت كلمة المرور', 'سجّل الدخول بكلمتك الجديدة')
      // الخادم أبطل كل الجلسات، فالخروج هو المسار الصحيح لا محاولة المتابعة
      await logout()
      void refreshProfile
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields) setErrors(err.fields)
        else setFormError(err.message)
      } else {
        setFormError('حدث خطأ غير متوقع')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4 py-10">
      <Card className="w-full max-w-md">
        <CardBody className="space-y-5 p-6">
          <div className="text-center">
            <LogoMark className="mx-auto size-16" />
            <span className="mx-auto mt-4 grid size-12 place-items-center rounded-full bg-warning-subtle text-warning">
              <ShieldAlert className="size-6" />
            </span>
            <h1 className="mt-3 font-display text-xl font-bold text-fg">غيّر كلمة المرور المؤقّتة</h1>
            <p className="mt-1.5 text-sm text-fg-muted">
              حسابك يستعمل كلمة مرور مؤقّتة معروفة. لا يمكن استعمال النظام قبل تغييرها.
            </p>
          </div>

          {formError && <Alert tone="danger">{formError}</Alert>}

          <form onSubmit={submit} noValidate className="space-y-4">
            <Input
              label="كلمة المرور الحالية" required type="password" autoFocus autoComplete="current-password"
              value={currentPassword} onChange={(e) => setCurrent(e.target.value)}
              error={errors.currentPassword}
              startIcon={<KeyRound className="size-4" />}
            />
            <Input
              label="كلمة المرور الجديدة" required type="password" autoComplete="new-password"
              value={newPassword} onChange={(e) => setNew(e.target.value)}
              error={errors.newPassword}
              hint="٨ أحرف على الأقل"
            />
            <Input
              label="تأكيد كلمة المرور" required type="password" autoComplete="new-password"
              value={confirmPassword} onChange={(e) => setConfirm(e.target.value)}
              error={errors.confirmPassword}
            />

            <Button type="submit" size="lg" fullWidth loading={busy}>حفظ ومتابعة</Button>
          </form>

          <Button variant="ghost" fullWidth onClick={() => void logout()}>
            تسجيل الخروج
          </Button>
        </CardBody>
      </Card>
    </div>
  )
}
